/* ATLAS — Copyright (C) 2026 ATLAS contributors. GPL-2.0-or-later; see LICENSE and NOTICE.md. */
import type { BrushDef, DecalDef, MapManifest, SurfaceKind, Vec3Tuple } from '@atlas/content';

/**
 * PRACTICE-01: a small ORIGINAL greybox arena for the offline combat practice page (PRD
 * Practice; task C2). It exists to exercise combat presentation: every weapon, ammunition,
 * health tier, armour tier and quad damage are placed, plus spawn points for the local player
 * and two scripted target dummies. It is a presentation test map, not a duel map design.
 *
 * One brush list is the single source of both the game map (collision, "map v1" text for
 * @atlas/game) and the rendered greybox (MapManifest for buildMapScene), so the two cannot
 * drift. All brushes are solid (CONTENTS_SOLID); there are no triggers.
 */

/** One solid brush of a local (practice or onboarding) map. */
export interface PracticeBrush {
  readonly min: Vec3Tuple;
  readonly max: Vec3Tuple;
  readonly surface: SurfaceKind;
  readonly rise?: '+x' | '-x' | '+y' | '-y';
}

const box = (min: Vec3Tuple, max: Vec3Tuple, surface: SurfaceKind): PracticeBrush => ({ min, max, surface });
const wedge = (min: Vec3Tuple, max: Vec3Tuple, rise: '+x' | '-x' | '+y' | '-y'): PracticeBrush => ({ min, max, surface: 'slope', rise });

/** Arena interior: x [-1024, 1024], y [-768, 768], floor top z 0, ceiling at z 448. */
export const PRACTICE_BOUNDS = { min: [-1024, -768, 0] as Vec3Tuple, max: [1024, 768, 448] as Vec3Tuple };

export const PRACTICE_BRUSHES: readonly PracticeBrush[] = [
  // shell
  box([-1088, -832, -64], [1088, 832, 0], 'floor'),
  box([-1088, -832, 0], [-1024, 832, 448], 'wall'),
  box([1024, -832, 0], [1088, 832, 448], 'wall'),
  box([-1024, -832, 0], [1024, -768, 448], 'wall'),
  box([-1024, 768, 0], [1024, 832, 448], 'wall'),
  box([-1088, -832, 448], [1088, 832, 512], 'ceiling'),
  // north bone platform with two ramps (walkable: 72 over 192); the east-west lane stays open
  box([-160, 224, 0], [160, 480, 72], 'platform'),
  wedge([-352, 288, 0], [-160, 416, 72], '+x'),
  wedge([160, 288, 0], [352, 416, 72], '-x'),
  // cover pillars and low walls
  box([-544, -416, 0], [-480, -352, 192], 'wall'),
  box([-544, 352, 0], [-480, 416, 192], 'wall'),
  box([480, -416, 0], [544, -352, 192], 'wall'),
  box([480, 352, 0], [544, 416, 192], 'wall'),
  box([-96, -560, 0], [96, -528, 48], 'stair'),
  box([-96, 528, 0], [96, 560, 48], 'stair'),
  // north-west ledge (128 high) reached by a ramp
  box([-1024, 576, 0], [-512, 768, 128], 'platform'),
  wedge([-800, 384, 0], [-704, 576, 128], '+y'),
];

const DECALS: readonly DecalDef[] = [
  // wayfinding strip to the weapon rack, ledge edge marking
  { min: [-960, -24, 0], max: [-576, 24, 0], color: 'wayfinding' },
  { min: [-1024, 568, 128], max: [-512, 576, 128], color: 'hazard' },
  { min: [-160, 216, 72], max: [160, 224, 72], color: 'measure-major' },
];

export type EntityDef = Readonly<Record<string, string>>;

const at = (x: number, y: number, z: number): string => `${x} ${y} ${z}`;

/** The local player's initial spawn (spawnflags 1) holds the weapon rack. */
export const PRACTICE_INITIAL_SPAWN: Vec3Tuple = [-768, 0, 24];

const RACK = ['weapon_shotgun', 'weapon_grenadelauncher', 'weapon_rocketlauncher', 'weapon_lightning', 'weapon_railgun', 'weapon_plasmagun', 'weapon_bfg'];

export const PRACTICE_ENTITIES: readonly EntityDef[] = [
  { classname: 'worldspawn', message: 'ATLAS practice arena PRACTICE-01 (greybox)' },
  // spawns
  { classname: 'info_player_deathmatch', origin: at(...PRACTICE_INITIAL_SPAWN), angle: '0', spawnflags: '1' },
  { classname: 'info_player_deathmatch', origin: at(-768, -512, 24), angle: '0' },
  { classname: 'info_player_deathmatch', origin: at(0, -640, 24), angle: '90' },
  { classname: 'info_player_deathmatch', origin: at(0, 640, 24), angle: '270' },
  { classname: 'info_player_deathmatch', origin: at(704, -224, 24), angle: '180' },
  { classname: 'info_player_deathmatch', origin: at(704, 224, 24), angle: '180' },
  // weapon rack on the initial spawn (picked up when the items first become solid)
  ...RACK.map((classname) => ({ classname, origin: at(...PRACTICE_INITIAL_SPAWN) })),
  // the same archetypes placed around the arena (pickup model presentation)
  { classname: 'weapon_machinegun', origin: at(-512, 0, 24) },
  { classname: 'weapon_shotgun', origin: at(-256, -448, 24) },
  { classname: 'weapon_grenadelauncher', origin: at(256, -448, 24) },
  { classname: 'weapon_rocketlauncher', origin: at(0, 352, 96) },
  { classname: 'weapon_lightning', origin: at(-256, 448, 24) },
  { classname: 'weapon_railgun', origin: at(-896, 672, 152) },
  { classname: 'weapon_plasmagun', origin: at(256, 448, 24) },
  { classname: 'weapon_bfg', origin: at(0, -704, 24) },
  // ammunition next to the weapons
  { classname: 'ammo_bullets', origin: at(-512, 96, 24) },
  { classname: 'ammo_shells', origin: at(-320, -448, 24) },
  { classname: 'ammo_grenades', origin: at(320, -448, 24) },
  { classname: 'ammo_rockets', origin: at(-416, -64, 24) },
  { classname: 'ammo_lightning', origin: at(-320, 448, 24) },
  { classname: 'ammo_slugs', origin: at(-832, 672, 152) },
  { classname: 'ammo_cells', origin: at(320, 448, 24) },
  { classname: 'ammo_bfg', origin: at(96, -704, 24) },
  // health tiers
  { classname: 'item_health_small', origin: at(-128, -320, 24) },
  { classname: 'item_health_small', origin: at(128, -320, 24) },
  { classname: 'item_health_small', origin: at(0, 160, 24) },
  { classname: 'item_health', origin: at(512, 0, 24) },
  { classname: 'item_health', origin: at(-512, 256, 24) },
  { classname: 'item_health_large', origin: at(-896, -640, 24) },
  { classname: 'item_health_mega', origin: at(-640, 704, 152) },
  // armour tiers
  { classname: 'item_armor_shard', origin: at(-288, 128, 24) },
  { classname: 'item_armor_shard', origin: at(-224, 128, 24) },
  { classname: 'item_armor_shard', origin: at(-224, 176, 24) },
  { classname: 'item_armor_combat', origin: at(0, -352, 24) },
  { classname: 'item_armor_body', origin: at(512, -640, 24) },
  // quad damage (first appears 30-60 s after the start, as the reference powerups do)
  { classname: 'item_quad', origin: at(896, 640, 24) },
];

/** CONTENTS_SOLID. */
const SOLID = 1;

function entityString(ents: readonly EntityDef[]): string {
  return ents.map((e) => `{\n${Object.entries(e).map(([k, v]) => `"${k}" "${v}"`).join('\n')}\n}`).join('\n');
}

/** A local map in the @atlas/game "map v1" text format (all brushes CONTENTS_SOLID). */
export function buildGameMapText(comment: string, brushes: readonly PracticeBrush[], entities: readonly EntityDef[]): string {
  const lines = ['map v1', `# ${comment}`];
  for (const b of brushes) {
    const c = [...b.min, ...b.max].join(' ');
    lines.push(b.rise ? `brush 0 wedge ${c} ${b.rise} ${SOLID}` : `brush 0 box ${c} ${SOLID}`);
  }
  lines.push('entities', entityString(entities), 'end');
  return `${lines.join('\n')}\n`;
}

/** Render-only manifest for buildMapScene (same brushes; no spawns, triggers, or zones). */
export function buildRenderManifest(id: string, name: string, brushes: readonly PracticeBrush[], decals: readonly DecalDef[]): MapManifest {
  const defs: BrushDef[] = brushes.map((b) =>
    b.rise ? { kind: 'wedge', min: b.min, max: b.max, rise: b.rise, surface: b.surface } : { kind: 'box', min: b.min, max: b.max, surface: b.surface },
  );
  return {
    format: 'atlas-map',
    formatVersion: 1,
    id,
    name,
    revision: 1,
    collisionVersion: 2,
    allowedModes: ['practice'],
    brushes: defs,
    spawns: [],
    jumpPads: [],
    teleporters: [],
    hazards: [],
    zones: [],
    decals,
  };
}

/** The practice map in the @atlas/game "map v1" text format. */
export function practiceGameMapText(): string {
  return buildGameMapText('ATLAS PRACTICE-01 (original greybox; generated from apps/client/src/practice/practiceMap.ts)', PRACTICE_BRUSHES, PRACTICE_ENTITIES);
}

/** Render-only manifest for buildMapScene (same brushes; no spawns, triggers, or zones). */
export function practiceRenderManifest(): MapManifest {
  return buildRenderManifest('practice-01', 'PRACTICE-01 (greybox)', PRACTICE_BRUSHES, DECALS);
}

/** Item classnames placed on the map (tests). */
export function practiceItemClassnames(): string[] {
  return PRACTICE_ENTITIES.map((e) => e.classname!).filter((c) => /^(weapon|ammo|item)_/.test(c));
}
